import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { createBulkSubscriptions } from '../services/api';

const TEMPLATE_HEADERS = [
  'name',
  'service',
  'duration',
  'customDays',
  'estimatedPrice',
  'currency',
  'startDate',
  'reminderDaysBefore',
  'notifyEmails',
  'notes',
  'active',
  'renewalUrl',
  'paymentCard',
];

const DURATIONS = ['monthly', 'quarterly', 'yearly', 'custom'];

/** Parse date from YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, or Excel serial */
const parseFlexibleDate = (value) => {
  if (value === undefined || value === null || value === '') return null;
  // Excel serial date (number) - Excel 1 = 1900-01-01, epoch = 1899-12-31
  if (typeof value === 'number' && !isNaN(value) && value > 0) {
    const epoch = new Date(1899, 11, 31);
    const date = new Date(epoch.getTime() + value * 86400000);
    return isNaN(date.getTime()) ? null : date;
  }
  // Already a Date
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  const s = String(value).trim();
  if (!s) return null;
  // ISO: YYYY-MM-DD
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (iso) {
    const d = new Date(parseInt(iso[1], 10), parseInt(iso[2], 10) - 1, parseInt(iso[3], 10));
    return isNaN(d.getTime()) ? null : d;
  }
  // DD/MM/YYYY or MM/DD/YYYY
  const slash = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
  if (slash) {
    const a = parseInt(slash[1], 10);
    const b = parseInt(slash[2], 10);
    const year = parseInt(slash[3], 10);
    let d = new Date(year, b - 1, a);
    if (!isNaN(d.getTime()) && d.getDate() === a && d.getMonth() === b - 1) return d;
    d = new Date(year, a - 1, b);
    if (!isNaN(d.getTime()) && d.getDate() === b && d.getMonth() === a - 1) return d;
    return null;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const validateRow = (row, index) => {
  const errors = [];
  const rowNum = index + 2; // Excel rows are 1-based + header

  if (!row.name || String(row.name).trim() === '') {
    errors.push('Name is required');
  }

  if (!row.service || String(row.service).trim() === '') {
    errors.push('Service is required');
  }

  const duration = (row.duration || 'monthly').toString().toLowerCase().trim();
  if (!DURATIONS.includes(duration)) {
    errors.push(`Duration must be one of: ${DURATIONS.join(', ')}`);
  } else if (duration === 'custom') {
    const customDays = parseInt(row.customDays, 10);
    if (isNaN(customDays) || customDays < 1) {
      errors.push('Custom days required when duration is custom');
    }
  }

  const price = parseFloat(row.estimatedPrice);
  if (isNaN(price) || price < 0) {
    errors.push('Valid estimated price is required');
  }

  const startDate = parseFlexibleDate(row.startDate);
  if (!startDate || isNaN(startDate.getTime())) {
    errors.push('Valid start date (YYYY-MM-DD or DD/MM/YYYY) is required');
  }

  const reminderDays = row.reminderDaysBefore !== undefined && row.reminderDaysBefore !== '' 
    ? parseInt(row.reminderDaysBefore, 10) 
    : 7;
  if (!isNaN(reminderDays) && (reminderDays < 0 || reminderDays > 30)) {
    errors.push('Reminder days must be 0-30');
  }

  return {
    valid: errors.length === 0,
    errors,
    rowNum,
  };
};

/** Build raw row object for validation/parsing */
const toRawRow = (sub) => ({
  name: sub.name,
  service: sub.service,
  duration: sub.duration,
  customDays: sub.duration === 'custom' ? (sub.customDays ?? '') : '',
  estimatedPrice: sub.estimatedPrice,
  currency: sub.currency,
  startDate: sub.startDate instanceof Date ? sub.startDate.toISOString().split('T')[0] : String(sub.startDate ?? ''),
  reminderDaysBefore: sub.reminderDaysBefore ?? 7,
  notifyEmails: Array.isArray(sub.notifyEmails) ? sub.notifyEmails.join(', ') : (sub.notifyEmails ?? ''),
  notes: sub.notes ?? '',
  active: sub.active ?? true,
  renewalUrl: sub.renewalUrl ?? '',
  paymentCard: sub.paymentCard ?? '',
});

const parseRowToSubscription = (row) => {
  const duration = (row.duration || 'monthly').toString().toLowerCase().trim();
  const startDate = parseFlexibleDate(row.startDate) || new Date();
  const customDays = row.customDays ? parseInt(row.customDays, 10) : null;
  
  let nextRenewal = new Date(startDate);
  switch (duration) {
    case 'monthly':
      nextRenewal.setMonth(nextRenewal.getMonth() + 1);
      break;
    case 'quarterly':
      nextRenewal.setMonth(nextRenewal.getMonth() + 3);
      break;
    case 'yearly':
      nextRenewal.setFullYear(nextRenewal.getFullYear() + 1);
      break;
    case 'custom':
      if (customDays) nextRenewal.setDate(nextRenewal.getDate() + customDays);
      break;
    default:
      nextRenewal.setMonth(nextRenewal.getMonth() + 1);
  }

  let notifyEmails = [];
  if (row.notifyEmails && String(row.notifyEmails).trim()) {
    notifyEmails = String(row.notifyEmails)
      .split(/[,;]/)
      .map(e => e.trim())
      .filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  }

  let active = true;
  if (row.active !== undefined && row.active !== '') {
    const v = String(row.active).toLowerCase();
    active = v === 'true' || v === 'yes' || v === '1';
  }

  return {
    name: String(row.name || '').trim(),
    service: String(row.service || '').trim(),
    duration: duration,
    customDays: duration === 'custom' ? customDays : null,
    estimatedPrice: parseFloat(row.estimatedPrice),
    currency: (row.currency || 'USD').toString().trim().substring(0, 3) || 'USD',
    startDate,
    nextRenewalDate: nextRenewal,
    reminderDaysBefore: isNaN(parseInt(row.reminderDaysBefore, 10)) ? 7 : Math.min(30, Math.max(0, parseInt(row.reminderDaysBefore, 10))),
    notifyEmails,
    notes: row.notes ? String(row.notes).trim() : '',
    renewalUrl: row.renewalUrl ? String(row.renewalUrl).trim() : '',
    paymentCard: row.paymentCard ? String(row.paymentCard).trim() : '',
    active,
  };
};

const EditableCell = ({
  row, rowIndex, field, editingCell, onEdit, onSave,
  display, rawValue, inputType = 'text', options, placeholder, className,
}) => {
  const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;
  const value = rawValue ?? display;

  const handleBlur = (e) => {
    const v = e.target.value;
    if (String(v).trim() !== String(value)) {
      onSave(rowIndex, field, inputType === 'number' ? (v === '' ? 0 : parseFloat(v) || 0) : v);
    }
    onEdit(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
    if (e.key === 'Escape') {
      onEdit(null);
      e.target.blur();
    }
  };

  if (isEditing) {
    if (options?.length) {
      return (
        <td className="px-3 py-1">
          <select
            autoFocus
            defaultValue={value}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            {options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </td>
      );
    }
    return (
      <td className="px-3 py-1">
        <input
          autoFocus
          type={inputType}
          defaultValue={value}
          placeholder={placeholder}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full min-w-[80px] px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        />
      </td>
    );
  }

  return (
    <td
      className={`px-3 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 ${className}`}
      onClick={() => onEdit({ rowIndex, field })}
      title="Click to edit"
    >
      {display}
    </td>
  );
};

const BulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | preview
  const [previewData, setPreviewData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, field }

  const downloadTemplate = () => {
    const template = [
      TEMPLATE_HEADERS,
      [
        'Mapbox API',
        'Mapbox',
        'monthly',
        '',
        '50',
        'USD',
        new Date().toISOString().split('T')[0],
        '7',
        'admin@example.com',
        'Optional notes',
        'true',
        'https://account.mapbox.com/billing',
        'Company Visa •••• 4242',
      ],
      [
        'AWS',
        'Amazon Web Services',
        'yearly',
        '',
        '1200',
        'USD',
        new Date().toISOString().split('T')[0],
        '14',
        'admin@example.com, dev@example.com',
        '',
        'true',
        'https://console.aws.amazon.com/billing/',
        'Corp Amex •••• 1234',
      ],
      [
        'Custom SaaS',
        'Vendor XYZ',
        'custom',
        '50',
        '99',
        'USD',
        new Date().toISOString().split('T')[0],
        '7',
        'admin@example.com',
        'Every 50 days (e.g. bi-monthly)',
        'true',
        'https://vendor-xyz.com/portal',
        'Personal card',
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    const colWidths = TEMPLATE_HEADERS.map((header) => {
      if (header === 'notifyEmails') return { wch: 35 };
      if (header === 'notes') return { wch: 32 };
      if (header === 'renewalUrl') return { wch: 40 };
      if (header === 'paymentCard') return { wch: 24 };
      return { wch: 16 };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Subscriptions');
    XLSX.writeFile(wb, 'subscriptions_template.xlsx');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

        if (rows.length === 0) {
          setError('File is empty. Please add at least one subscription row.');
          return;
        }

        const validated = rows.map((row, i) => {
          const validation = validateRow(row, i);
          const subscription = parseRowToSubscription(row);
          const raw = toRawRow(subscription);
          return {
            ...subscription,
            _raw: raw,
            _rowNum: validation.rowNum,
            _valid: validation.valid,
            _errors: validation.errors,
          };
        });

        setPreviewData(validated);
        setStep('preview');
      } catch (err) {
        setError('Failed to parse file. Please ensure it is a valid Excel (.xlsx) file.');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleProceed = async () => {
    const validItems = previewData.filter((d) => d._valid);
    if (validItems.length === 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = validItems.map(({ _rowNum, _valid, _errors, ...sub }) => sub);
      const res = await createBulkSubscriptions(payload);
      onSuccess?.();
      onClose();
      setStep('upload');
      setPreviewData([]);
      if (res.data?.errors?.length) {
        setError(`Created ${res.data.created}, failed ${res.data.failed}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create subscriptions');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('upload');
    setPreviewData([]);
    setError(null);
    setEditingCell(null);
    onClose();
  };

  const handleCellEdit = (rowIndex, field, value) => {
    setPreviewData((prev) => {
      const next = [...prev];
      const row = next[rowIndex];
      if (!row || !row._raw) return prev;
      const raw = { ...row._raw, [field]: value };
      const validation = validateRow(raw, rowIndex);
      const subscription = parseRowToSubscription(raw);
      next[rowIndex] = {
        ...subscription,
        _raw: raw,
        _rowNum: validation.rowNum,
        _valid: validation.valid,
        _errors: validation.errors,
      };
      return next;
    });
  };

  const validCount = previewData.filter((d) => d._valid).length;
  const invalidCount = previewData.length - validCount;
  const canProceed = validCount > 0 && !submitting;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {step === 'upload' ? 'Bulk Upload Subscriptions' : 'Preview & Confirm'}
              </h2>
              <button
                onClick={handleClose}
                className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {step === 'upload' && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={downloadTemplate}
                      className="flex-1 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Template
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Choose File to Upload
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                    <p className="font-semibold">Template columns:</p>
                    <p>
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">name</code>,{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">service</code>,{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">duration</code> (monthly/quarterly/yearly/custom),{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">customDays</code>,{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">estimatedPrice</code>,{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">currency</code>,{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">startDate</code> (YYYY-MM-DD or DD/MM/YYYY),{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">reminderDaysBefore</code>,{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">notifyEmails</code> (comma-separated),{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">notes</code>,{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">active</code> (true/false),{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">renewalUrl</code> (optional),{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">paymentCard</code> (optional).
                      {' '}Click any field in the preview to edit it.
                    </p>
                  </div>
                </div>
              )}

              {step === 'preview' && (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
                      <span className="w-2 h-2 rounded-full bg-green-500 mr-2" />Valid: {validCount}
                    </span>
                    {invalidCount > 0 && (
                      <span className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                        <span className="w-2 h-2 rounded-full bg-red-500 mr-2" />Invalid: {invalidCount}
                      </span>
                    )}
                  </div>
                  <div className="border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden shadow-inner flex flex-col max-h-[50vh]">
                    <div className="overflow-auto flex-1 min-h-0">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-700/80 sticky top-0 z-10">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">Row</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">Status</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">Name</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">Service</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">Price</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">Duration</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">Start Date</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">Remind</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">Errors</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                          {previewData.map((row, i) => (
                            <tr
                              key={i}
                              className={row._valid ? 'bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800' : 'bg-red-50 dark:bg-red-900/20'}
                            >
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-300 font-medium">{row._rowNum}</td>
                              <td className="px-3 py-2">
                                {row._valid ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">✓ OK</span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">✗ Invalid</span>
                                )}
                              </td>
                              <EditableCell
                                row={row}
                                rowIndex={i}
                                field="name"
                                editingCell={editingCell}
                                onEdit={setEditingCell}
                                onSave={handleCellEdit}
                                display={row.name}
                                className="font-semibold text-gray-900 dark:text-white"
                              />
                              <EditableCell
                                row={row}
                                rowIndex={i}
                                field="service"
                                editingCell={editingCell}
                                onEdit={setEditingCell}
                                onSave={handleCellEdit}
                                display={row.service}
                                className="text-gray-600 dark:text-gray-300"
                              />
                              <EditableCell
                                row={row}
                                rowIndex={i}
                                field="estimatedPrice"
                                editingCell={editingCell}
                                onEdit={setEditingCell}
                                onSave={handleCellEdit}
                                display={`${row.currency} ${row.estimatedPrice}`}
                                rawValue={row._raw?.estimatedPrice}
                                inputType="number"
                                className="font-semibold text-emerald-600 dark:text-emerald-400"
                              />
                              <EditableCell
                                row={row}
                                rowIndex={i}
                                field="duration"
                                editingCell={editingCell}
                                onEdit={setEditingCell}
                                onSave={handleCellEdit}
                                display={row.duration}
                                rawValue={row._raw?.duration}
                                options={DURATIONS}
                                className="text-gray-600 dark:text-gray-300 capitalize"
                              />
                              <EditableCell
                                row={row}
                                rowIndex={i}
                                field="startDate"
                                editingCell={editingCell}
                                onEdit={setEditingCell}
                                onSave={handleCellEdit}
                                display={row.startDate instanceof Date ? row.startDate.toLocaleDateString() : String(row.startDate ?? '')}
                                rawValue={row._raw?.startDate}
                                placeholder="YYYY-MM-DD or DD/MM/YYYY"
                                className="text-gray-600 dark:text-gray-300"
                              />
                              <EditableCell
                                row={row}
                                rowIndex={i}
                                field="reminderDaysBefore"
                                editingCell={editingCell}
                                onEdit={setEditingCell}
                                onSave={handleCellEdit}
                                display={row.reminderDaysBefore}
                                rawValue={row._raw?.reminderDaysBefore}
                                inputType="number"
                                className="text-gray-600 dark:text-gray-300"
                              />
                              <td className="px-3 py-2 text-red-600 dark:text-red-400 text-xs max-w-[180px]">
                                {row._errors?.length ? row._errors.join('; ') : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Only valid rows will be created. Fix invalid rows in your file and re-upload if needed.
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mx-6 mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            {step === 'preview' && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center gap-4">
                <button
                  onClick={() => { setStep('upload'); setPreviewData([]); setError(null); setEditingCell(null); }}
                  className="px-5 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleProceed}
                  disabled={!canProceed}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold shadow-lg shadow-blue-600/20"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      Proceed & Create {validCount} Subscription{validCount !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BulkUploadModal;
